import {
  normalizeMeasuredQuantity,
  parseDecimalInput,
  validateMeasuredQuantity
} from '@washday/utilities';

export type OrderQuantityType = 'LENGTH' | 'WEIGHT';

const PRECISION: Record<OrderQuantityType, number> = {
  LENGTH: 3,
  WEIGHT: 2
};

function getPrecision(type: OrderQuantityType): number {
  return PRECISION[type];
}

function parseValue(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return parseDecimalInput(value);
}

/**
 * Normalizes a quantity value for order lines.
 * LENGTH: 3 decimal places. WEIGHT: 2 decimal places.
 */
export function normalizeOrderQuantity(
  value: number | string | undefined | null,
  type: OrderQuantityType
): number | null {
  const parsed = parseValue(value);
  if (parsed === null) return null;
  const precision = getPrecision(type);
  if (type === 'LENGTH') {
    const result = validateMeasuredQuantity(parsed, {
      precision,
      allowNegative: true,
      allowZero: true
    });
    return result.ok ? result.value : null;
  }
  return normalizeMeasuredQuantity(parsed, precision);
}

export type ValidateOrderQuantityResult =
  | { ok: true; value: number }
  | { ok: false; error: string };

/**
 * Validates a quantity value for order lines.
 * LENGTH: up to 3 decimal places. WEIGHT: up to 2 decimal places.
 */
export function validateOrderQuantity(
  value: number | string | undefined | null,
  type: OrderQuantityType
): ValidateOrderQuantityResult {
  const parsed = parseValue(value);
  if (parsed === null) {
    return { ok: false, error: 'invalid_quantity' };
  }
  const precision = getPrecision(type);
  const result = validateMeasuredQuantity(parsed, {
    precision,
    allowNegative: true,
    allowZero: true
  });
  if (result.ok) {
    return { ok: true, value: result.value };
  }
  return { ok: false, error: result.error };
}
